import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  AssociateSetupContext,
  AuthenticationContext,
  OnboardingSteps,
  ProfileStep,
  ProfileStepScreenName,
  useCompleteAssociateOnboardingStep,
} from '@vs-online-only-app/state';
import EnterEmployeeId from './steps/EnterEmployeeId';
import EnterFullName from './steps/EnterFullName';
import SelectLanguages from './steps/SelectLanguages';
import SelectStore from './steps/SelectStore';
import SetProfilePicture from './steps/profile-picture/SetProfilePicture';

interface ProfileStepWithBody extends ProfileStep {
  Body: FC;
}

type ProfileSetupState = {
  currentStep: ProfileStepWithBody;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  currentStepIndex: number;
  nrOfSteps: number;
  canGoToPreviousStep: boolean;
  completeProfileSetup: () => Promise<void>;
};

const profileSetupComponentsMap: Record<ProfileStepScreenName, FC> = {
  [ProfileStepScreenName.fullName]: EnterFullName,
  [ProfileStepScreenName.languages]: SelectLanguages,
  [ProfileStepScreenName.employeeId]: EnterEmployeeId,
  [ProfileStepScreenName.profilePicture]: SetProfilePicture,
  [ProfileStepScreenName.storeSelector]: SelectStore,
};

// This is the order that the step screens should be displayed in,
// consistent with the mobile app
const profileSetupStepsOrder = [
  ProfileStepScreenName.employeeId,
  ProfileStepScreenName.fullName,
  ProfileStepScreenName.languages,
  ProfileStepScreenName.storeSelector,
  ProfileStepScreenName.profilePicture,
];

const sortSteps = (a: ProfileStep, b: ProfileStep) =>
  profileSetupStepsOrder.indexOf(a.screenName) -
  profileSetupStepsOrder.indexOf(b.screenName);

const ProfileSetupContext = createContext<ProfileSetupState>(
  {} as ProfileSetupState,
);

const ProfileSetupProvider: FC<PropsWithChildren> = (props) => {
  const { associateSetupState, refetchSetupState } = useContext(
    AssociateSetupContext,
  );
  const { refetchUser } = useContext(AuthenticationContext);
  const completeAssociateOnboardingStep = useCompleteAssociateOnboardingStep();

  const steps: ProfileStepWithBody[] = useMemo(() => {
    if (!associateSetupState?.profileSteps) return [];

    return associateSetupState.profileSteps.sort(sortSteps).map((step) => ({
      ...step,
      Body: profileSetupComponentsMap[step.screenName],
    }));
  }, [associateSetupState]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const goToNextStep = useCallback(() => {
    if (currentStepIndex === steps.length - 1) return;
    setCurrentStepIndex((prev) => prev + 1);
  }, [currentStepIndex, steps.length]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex === 0) return;
    setCurrentStepIndex((prev) => prev - 1);
  }, [currentStepIndex]);

  const completeProfileSetup = useCallback(async () => {
    await completeAssociateOnboardingStep.mutateAsync({
      onboardingStep: OnboardingSteps.profileSetup,
    });
    await Promise.all([refetchSetupState(), refetchUser()]);
  }, [completeAssociateOnboardingStep, refetchSetupState, refetchUser]);

  const value: ProfileSetupState = useMemo(
    () => ({
      currentStep: steps[currentStepIndex],
      goToNextStep,
      goToPreviousStep,
      currentStepIndex,
      nrOfSteps: steps.length,
      canGoToPreviousStep: currentStepIndex > 0,
      completeProfileSetup,
    }),
    [
      completeProfileSetup,
      currentStepIndex,
      goToNextStep,
      goToPreviousStep,
      steps,
    ],
  );

  return (
    <ProfileSetupContext.Provider value={value}>
      {props.children}
    </ProfileSetupContext.Provider>
  );
};

export { ProfileSetupProvider, ProfileSetupContext };
